import React, {useEffect, useMemo, useState} from "react";
import FeatherIcon from "feather-icons-react";
import {useTranslation} from "react-i18next";
import useAxios from "../../utils/useAxios";
import PreImage from "../PreImage";
import ErrorMessage from "../ErrorMessage";
import {VALID_EXTENSIONS} from "../../constants";
import normalizeImageFile from "../../utils/normalizeImageFile";

const FormImageUpload = ({path, id, file, setFile, width, height, name = "Image", canWrite= true}) => {
    const {t} = useTranslation();
    const api = useAxios();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    // Memoised + revoked, not created inline in the JSX below: called straight
    // from render it minted a fresh blob URL on EVERY render and never released
    // any of them, so each re-render leaked the whole photo and PreImage saw a
    // new `src` and restarted its load. Adding photos to player after player
    // piled those up for the lifetime of the page.
    const previewSrc = useMemo(
        () => (file?.file ? URL.createObjectURL(file.file) : (typeof file === "string" ? file : file || null)),
        [file]
    );
    useEffect(() => {
        if (file?.file && previewSrc) return () => URL.revokeObjectURL(previewSrc);
    }, [file, previewSrc]);

    const handleRemoveImage = async () => {
        const confirmed = window.confirm(t("Are you sure you want to delete the image?"));
        if (confirmed) {
            const formDataToSend = new FormData();
            formDataToSend.append("image", "");

            setUploading(true);
            try {
                await api.patch(`/api/v1.0/${path}/${id}/`, formDataToSend);
                setFile(null);
            } catch (error) {
                console.error("Image remove error", error);
            } finally {
                setUploading(false);
            }
        }
    };

    const handleFileChange = async (e) => {
        const selected = e.target.files[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop().toLowerCase();
        if (!VALID_EXTENSIONS.includes(extension)) {
            setError("Unsupported file type. Supported formats are: JPG, PNG, JPEG");
            setFile(null);
            return;
        }

        setError("");

        // Goes through normalizeImageFile rather than reading img.width/height
        // off the raw file: a portrait iPhone photo is landscape pixels + an
        // EXIF rotation tag, so the raw dimensions describe a landscape image
        // (which PreImage then used for its aspect-ratio box) and the pixels
        // themselves upload sideways. This bakes the rotation in and hands
        // back the post-rotation dimensions, so preview box, preview image and
        // the stored file all agree. It also downscales, which is what makes
        // uploading photo after photo on a phone quick.
        setUploading(true);
        try {
            const { file: normalized, width, height } = await normalizeImageFile(selected);
            setFile({ file: normalized, width, height });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="form-group form-image">
            <label>{t(name)}</label>
            {canWrite && file && <FeatherIcon icon="x" onClick={handleRemoveImage} style={{cursor: "pointer"}}/>}
            {file ?
                <PreImage
                    src={previewSrc}
                    width={file.width || width}
                    height={file.height || height}
                    marginTop={20}
                />
                :
                <div className="file-preview materials__container">
                    {uploading ? (
                            <>
                                <FeatherIcon icon="loader"/>
                                <p>{t("Loading")}...</p>
                            </>
                        ) :
                        <>
                            <FeatherIcon icon="image"/>
                            <p>
                                {t("Supported formats")}:<br/>
                                JPG, PNG, GIF
                            </p>
                        </>
                    }
                    <input
                        type="file"
                        onChange={handleFileChange}
                        style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer"
                        }}
                        disabled={!canWrite}
                    />
                </div>
            }
            <ErrorMessage error={error && t(error)}/>
        </div>
    );
};

export default FormImageUpload;
